import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoadingIndicator, BusyContent } from '../ui/Primitives';
import { Link, useSearchParams } from 'react-router-dom';
import AvailabilityTable from './AvailabilityTable';
import axios from 'axios';
import "./FeatureSection.css";
import "./AvailabilityModal.css";
import FeatureAppointmentSchedulingRequirements from './FeatureAppointmentSchedulingRequirements';
import SchedulePicker from '../SchedulePickerComponents/SchedulePicker';
import SelectVoiceSection from './SelectVoiceSection';
import GreetingSection from './GreetingSection';
import LanguageSection from './LanguageSection';
import flashMessage from '../HelperFunctions';
import WorkflowManager from './WorkflowManager';
import ReceptionistPublishPanel from './ReceptionistPublishPanel';
import MeetingLocationSettings, { MeetingConfirmationSettings } from './MeetingLocationSettings';
import { logSectionView } from '../../utils/logger';
import {
  discardReceptionistDraft,
  getReceptionistConfiguration,
  publishReceptionistDraft,
  saveReceptionistDraft,
} from '../../services/receptionistConfigApi';


const receptionistDraftFields = {
  calendarProvider: 'Calendar provider',
  greeting: 'Greeting',
  voiceId: 'Voice',
  languageSettings: 'Default language',
  availability: 'Appointment hours',
  timezone: 'Timezone',
  duration: 'Meeting length',
  leadTime: 'Advance notice',
  calendarId: 'Calendar event type',
  meetingSettings: 'Meeting location and invitations',
};

function withLocalDraft(configuration, changes) {
  if (!configuration) return configuration;
  const draft = { ...configuration.draft, ...changes };
  const receptionistFieldKeys = new Set(Object.keys(receptionistDraftFields));
  const serverFields = (configuration.changedFields || []).filter(
    field => !receptionistFieldKeys.has(field.key),
  );
  const localFields = (keys, group, category) => keys.flatMap(key => (
    JSON.stringify(draft[key]) !== JSON.stringify(configuration.published[key])
      ? [{
        key,
        label: receptionistDraftFields[key],
        published: configuration.published[key],
        draft: draft[key],
        group,
        category,
      }]
      : []
  ));
  const serviceFields = serverFields.filter(field => String(field.key).startsWith('service:'));
  const workflowFields = serverFields.filter(field => String(field.key).startsWith('workflow:'));
  const otherFields = serverFields.filter(field => (
    !String(field.key).startsWith('service:')
    && !String(field.key).startsWith('workflow:')
  ));
  const changedFields = [
    ...localFields(['greeting', 'voiceId', 'languageSettings'], 'Greeting and voice', 'Receptionist basics'),
    ...serviceFields,
    ...workflowFields,
    ...localFields(
      ['calendarProvider', 'calendarId', 'meetingSettings', 'availability', 'timezone', 'duration', 'leadTime'],
      'Appointment settings',
      'Built-in service details',
    ),
    ...otherFields,
  ];
  return {
    ...configuration,
    draft,
    changedFields,
    hasUnpublishedChanges: Boolean(changedFields.length),
  };
}

function FeatureSection({ organization, orgMember, setupGoal, onBusyChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsTab = searchParams.get('tab') === 'handling' ? 'handling' : 'voice';
  const setSettingsTab = tab => {
    if (setupGoal || tab === settingsTab) return;
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('tab', tab);
      return next;
    }, { preventScrollReset: true });
  };
  const settingsTabRefs = useRef({});
  const handleSettingsTabKey = (event, current) => {
    const tabs = ['voice', 'handling'];
    let next;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') next = tabs[(tabs.indexOf(current) + 1) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[1];
    else return;
    event.preventDefault();
    setSettingsTab(next);
    settingsTabRefs.current[next]?.focus();
  };
  // Modal state for availability
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  // Local organization state for modal
  const [localOrganization, setLocalOrganization] = useState(organization);
  // Latest org state for display
  const [latestOrganization, setLatestOrganization] = useState(organization);
  // Persist activeTab in localStorage
  const getInitialTab = () => {
    if (setupGoal) return setupGoal === 'appointment' ? 'schedule' : setupGoal;
    try {
      const savedTab = localStorage.getItem('featureSectionActiveTab');
      return savedTab || 'message';
    } catch {
      return 'message';
    }
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [isGoogleConnected, setIsGoogleConnected] = useState(Boolean(organization.is_google_connected));
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [calendarSettingsError, setCalendarSettingsError] = useState('');
  const [calendarAction, setCalendarAction] = useState('');
  const [isCustomWorkflowOpen, setIsCustomWorkflowOpen] = useState(false);
  const [receptionistConfiguration, setReceptionistConfiguration] = useState(null);
  const [receptionistConfigError, setReceptionistConfigError] = useState('');
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishReviewOpen, setPublishReviewOpen] = useState(false);
  const [isOpeningPublishReview, setIsOpeningPublishReview] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [workflowRefreshVersion, setWorkflowRefreshVersion] = useState(0);
  const [voiceNames, setVoiceNames] = useState({});
  const [draftSaveStatus, setDraftSaveStatus] = useState('saved');
  const [workflowBusy, setWorkflowBusy] = useState(false);
  useEffect(() => { onBusyChange?.(Boolean(workflowBusy || calendarAction || !receptionistConfiguration || ['pending', 'saving', 'error'].includes(draftSaveStatus))); }, [workflowBusy, calendarAction, receptionistConfiguration, draftSaveStatus, onBusyChange]);
  const localDraftRef = useRef({});
  const pendingDraftRef = useRef({});
  const draftSaveTimerRef = useRef(null);
  const activeDraftSaveRef = useRef(Promise.resolve());
  const calendarSettingsRequestRef = useRef(null);
  const calendarAbortRef = useRef(null);
  const calendarMountedRef = useRef(false);
  useEffect(() => {
    calendarMountedRef.current = true;
    return () => {
      calendarMountedRef.current = false;
      calendarAbortRef.current?.abort();
    };
  }, []);
  const providerReviewShownRef = useRef(false);
  const isReceptionistAdmin = Boolean(orgMember?.is_org_admin || orgMember?.is_admin);

  const [isCalendlyConnected, setIsCalendlyConnected] = useState(Boolean(organization.is_calendly_connected));

  const [isCallScreeningEnabled, setIsCallScreeningEnabled] = useState(organization.can_screen);
  const [isScheduleTextConfirmEnabled, setIsScheduleTextConfirmEnabled] = useState(
    organization.can_schedule_text_confirm
  );

  const acceptReceptionistConfiguration = useCallback((nextConfiguration) => {
    localDraftRef.current = nextConfiguration.draft || {};
    const confirmationText = nextConfiguration.draftServices?.appointment?.confirmationText;
    if (typeof confirmationText === 'boolean') {
      setIsScheduleTextConfirmEnabled(confirmationText);
    }
    setReceptionistConfiguration(current => (
      !current || nextConfiguration.draftVersion >= current.draftVersion
        ? nextConfiguration
        : current
    ));
  }, []);

  const handleVoiceOptionsLoaded = useCallback((options) => {
    setVoiceNames(Object.fromEntries((options || []).map(voice => [String(voice.id), voice.name])));
  }, []);

  useEffect(() => {
    let active = true;
    getReceptionistConfiguration()
      .then(result => {
        if (active) acceptReceptionistConfiguration(result);
      })
      .catch(error => {
        if (active) {
          setReceptionistConfigError(
            error.response?.data?.error?.message
            || 'Publishing status could not be loaded. Reload this page before editing.',
          );
        }
      });
    return () => { active = false; };
  }, [acceptReceptionistConfiguration]);

  useEffect(() => () => clearTimeout(draftSaveTimerRef.current), []);

  useEffect(() => {
    if (!setupGoal && isReceptionistAdmin && !providerReviewShownRef.current
        && receptionistConfiguration?.changedFields?.some(field => field.key === 'calendarProvider')) {
      providerReviewShownRef.current = true;
      setPublishReviewOpen(true);
    }
  }, [isReceptionistAdmin, receptionistConfiguration]);

  const persistPendingDraft = useCallback(() => {
    const changes = pendingDraftRef.current;
    if (!Object.keys(changes).length) return activeDraftSaveRef.current;
    pendingDraftRef.current = {};
    setDraftSaveStatus('saving');
    setPublishSuccess(false);
    setReceptionistConfigError('');
    const request = activeDraftSaveRef.current
      .catch(() => {})
      .then(() => saveReceptionistDraft(changes))
      .then(result => {
        setReceptionistConfiguration(current => {
          const latest = !current || result.draftVersion >= current.draftVersion
            ? result
            : current;
          return withLocalDraft(latest, localDraftRef.current);
        });
        setDraftSaveStatus(
          Object.keys(pendingDraftRef.current).length ? 'pending' : 'saved',
        );
        return result;
      })
      .catch(error => {
        pendingDraftRef.current = {
          ...changes,
          ...pendingDraftRef.current,
        };
        setDraftSaveStatus('error');
        setReceptionistConfigError(
          error.response?.data?.error?.message || 'The draft could not be saved.',
        );
        throw error;
      });
    activeDraftSaveRef.current = request;
    return request;
  }, []);

  const queueDraft = useCallback((changes, saveImmediately = false) => {
    localDraftRef.current = { ...localDraftRef.current, ...changes };
    pendingDraftRef.current = { ...pendingDraftRef.current, ...changes };
    setReceptionistConfiguration(current => withLocalDraft(current, changes));
    setDraftSaveStatus('pending');
    setPublishSuccess(false);
    setReceptionistConfigError('');
    clearTimeout(draftSaveTimerRef.current);
    if (saveImmediately) {
      return persistPendingDraft();
    } else {
      draftSaveTimerRef.current = setTimeout(() => {
        persistPendingDraft().catch(() => {});
      }, 800);
    }
    return Promise.resolve();
  }, [persistPendingDraft]);

  const flushDraft = useCallback(async () => {
    clearTimeout(draftSaveTimerRef.current);
    await persistPendingDraft();
    await activeDraftSaveRef.current;
    if (Object.keys(pendingDraftRef.current).length) {
      await persistPendingDraft();
    }
  }, [persistPendingDraft]);

  const refreshReceptionistConfiguration = useCallback(async () => {
    try {
      await flushDraft();
      const result = await getReceptionistConfiguration();
      acceptReceptionistConfiguration(result);
      return result;
    } catch (error) {
      setReceptionistConfigError(
        error.response?.data?.error?.message || 'Publishing status could not be refreshed.',
      );
      return null;
    }
  }, [acceptReceptionistConfiguration, flushDraft]);

  const openPublishReview = async () => {
    setIsOpeningPublishReview(true);
    try {
      await flushDraft();
      setPublishReviewOpen(true);
    } catch (_saveError) {
    } finally {
      setIsOpeningPublishReview(false);
    }
  };

  const publishDraft = async () => {
    if (!receptionistConfiguration) return;
    setIsPublishing(true);
    setPublishSuccess(false);
    setReceptionistConfigError('');
    try {
      const result = await publishReceptionistDraft(
        receptionistConfiguration.draftVersion,
      );
      acceptReceptionistConfiguration(result);
      setWorkflowRefreshVersion(current => current + 1);
      setPublishReviewOpen(false);
      setPublishSuccess(true);
    } catch (error) {
      const apiError = error.response?.data?.error;
      const fieldErrors = (apiError?.details?.errors || []).map(item => item.message).filter(Boolean);
      setReceptionistConfigError(
        [apiError?.message || 'The changes could not be published.', ...new Set(fieldErrors)].join(' '),
      );
      if (error.response?.status === 409) {
        try {
          acceptReceptionistConfiguration(await getReceptionistConfiguration());
        } catch (_refreshError) {}
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const discardDraft = async () => {
    if (!window.confirm('Discard all unpublished Receptionist changes?')) return;
    clearTimeout(draftSaveTimerRef.current);
    pendingDraftRef.current = {};
    setIsDiscarding(true);
    setPublishSuccess(false);
    setReceptionistConfigError('');
    try {
      await activeDraftSaveRef.current.catch(() => {});
      acceptReceptionistConfiguration(await discardReceptionistDraft());
      setWorkflowRefreshVersion(current => current + 1);
      setDraftSaveStatus('saved');
      setPublishReviewOpen(false);
    } catch (error) {
      setReceptionistConfigError(
        error.response?.data?.error?.message || 'The draft could not be discarded.',
      );
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleToggleFeature = async (feature) => {
    const jwt_token = localStorage.getItem('jwt_authorization');
    let payload = { jwt_token };
    setCalendarAction(feature);

    if (feature === 'callScreening') {
      payload.can_screen = !isCallScreeningEnabled;
      setIsCallScreeningEnabled(!isCallScreeningEnabled);
    }
    if (feature === 'scheduleTextConfirm') {
      const nextValue = !isScheduleTextConfirmEnabled;
      setIsScheduleTextConfirmEnabled(nextValue);
      try {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_HOST}/v2/receptionist/workflows/builtin:appointment`,
          { behavior: { confirmationText: nextValue } },
          { headers: { Authorization: `Bearer ${jwt_token}` } },
        );
        await refreshReceptionistConfiguration();
        flashMessage('success', 'Confirmation text setting added to your draft');
      } catch (error) {
        setIsScheduleTextConfirmEnabled(!nextValue);
        flashMessage('error', 'The confirmation text setting could not be saved');
      } finally {
        setCalendarAction('');
      }
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_HOST}/user`, payload);
      flashMessage('success', 'Updated successfully');
    } catch (error) {
      console.error('Failed to update feature:', error);
      flashMessage('error', 'Failed to update feature');
    } finally {
      setCalendarAction('');
    }
  };

  const handleEventTypeChange = async (event) => {
    const selectedEventUri = event.target.value;
    if (!selectedEventUri) return;
    setCalendarAction('event-type');
    try {
      await queueDraft({ calendarId: selectedEventUri }, true);
      await getCalendarSettings(selectedEventUri);
      flashMessage('success', 'Event type added to your Receptionist draft');
    } catch (error) {
      flashMessage('error', 'The event type could not be saved');
    } finally {
      setCalendarAction('');
    }
  };

  const getCalendarSettings = async (eventTypeUri) => {
    if (!isReceptionistAdmin || !calendarMountedRef.current) return;
    if (calendarSettingsRequestRef.current) await calendarSettingsRequestRef.current;
    if (!calendarMountedRef.current) return;
    calendarAbortRef.current?.abort();
    const controller = new AbortController();
    calendarAbortRef.current = controller;
    setCalendarSettingsError('');
    const jwt_token = localStorage.getItem('jwt_authorization');
    setCalendarAction(current => current || 'calendar-settings');
    const request = (async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_HOST}/v2/organization/${organization.id}/calendar_settings`,
          { op: 'get', eventTypeUri: eventTypeUri ?? localDraftRef.current.calendarId, jwt_token },
          { signal: controller.signal }
        );
        if (!calendarMountedRef.current || controller.signal.aborted) return;
        if (response.status === 200) setCalendarSettings(response.data);
      } catch (error) {
        if (!calendarMountedRef.current || controller.signal.aborted || axios.isCancel(error)) return;
        if (error.response?.status === 409) {
          setCalendarSettings(null);
          setIsCalendlyConnected(false);
          setLatestOrganization(current => ({ ...current, is_calendly_connected: false }));
          setCalendarSettingsError('Reconnect Calendly in Integrations to continue.');
        } else {
          setCalendarSettings(null);
          setCalendarSettingsError('Calendar settings could not be loaded. Please try again.');
        }
      } finally {
        if (calendarAbortRef.current === controller) {
          calendarSettingsRequestRef.current = null;
          if (calendarMountedRef.current) setCalendarAction(current => current === 'calendar-settings' ? '' : current);
        }
      }
    })();
    calendarSettingsRequestRef.current = request;
    return request;
  };

  useEffect(() => {
    if (organization.is_calendly_connected || organization.is_google_connected) {
      getCalendarSettings();
    }

    // keep local UI in sync if org prop changes
    setIsCallScreeningEnabled(organization.can_screen);
    setIsScheduleTextConfirmEnabled(organization.can_schedule_text_confirm);

    setIsGoogleConnected(Boolean(organization.is_google_connected));
    setIsCalendlyConnected(Boolean(organization.is_calendly_connected));
    setLatestOrganization(organization);
  }, [organization, isReceptionistAdmin]);

  useEffect(() => {
    const selected = receptionistConfiguration?.draft?.calendarId;
    if (isReceptionistAdmin && calendarSettings?.provider === 'calendly' && typeof selected === 'string' && (calendarSettings.eventType?.uri || '') !== selected) {
      getCalendarSettings(selected);
    }
  }, [receptionistConfiguration?.draft?.calendarId]);

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('featureSectionActiveTab', activeTab);
    } catch {}
    logSectionView('Receptionist', isCustomWorkflowOpen ? 'custom-workflow' : activeTab);
  }, [activeTab, isCustomWorkflowOpen]);

  // Determine if both are disconnected
  const isBothDisconnected = !isGoogleConnected && !isCalendlyConnected;
  const appointmentDraft = receptionistConfiguration?.draft || {};
  const calendarEventTypes = calendarSettings?.calendly?.event_types?.collection || [];
  const selectedCalendarId = appointmentDraft.calendarId ?? organization.calendly_calendar_id ?? '';
  const calendarEventNames = Object.fromEntries(calendarEventTypes.map(event => [
    event.uri, `${event.name}${event.duration ? ` (${event.duration} min)` : ''}`,
  ]));
  const appointmentDraftOrganization = {
    ...latestOrganization,
    ...localOrganization,
    availability: appointmentDraft.availability ?? localOrganization?.availability,
    timezone: appointmentDraft.timezone ?? localOrganization?.timezone,
    duration: appointmentDraft.duration ?? localOrganization?.duration,
    lead_time: appointmentDraft.leadTime ?? localOrganization?.lead_time,
  };

  const openAvailabilityModal = async () => {
    setCalendarAction('availability');
    try {
      const jwt_token = localStorage.getItem('jwt_authorization');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_HOST}/v2/organization/${organization.id}`,
        { jwt_token, op: 'get' },
      );
      setLocalOrganization(response.status === 200 ? response.data : organization);
    } catch (_error) {
      setLocalOrganization(organization);
    } finally {
      setCalendarAction('');
      setShowAvailabilityModal(true);
    }
  };

  const voiceFieldKeys = new Set(['greeting', 'voiceId', 'languageSettings']);
  const changedFields = receptionistConfiguration?.changedFields || [];
  const voiceChanges = changedFields.filter(field => voiceFieldKeys.has(field.key)).length;
  const handlingChanges = changedFields.filter(field => !voiceFieldKeys.has(field.key)).length;
  const selectBuiltInWorkflow = (outcomeType) => {
    const tab = outcomeType === 'appointment' ? 'schedule' : outcomeType === 'transfer' ? 'transfer' : 'message';
    setActiveTab(tab);
  };

  return (
    <div className="assign-tasks-section">
      <div className={`receptionist-workspace-grid${setupGoal ? ' onboarding-service-settings' : ''}`}>
        {!setupGoal && <aside className="receptionist-publish-rail" aria-label="Receptionist publishing">
          <ReceptionistPublishPanel
            configuration={receptionistConfiguration}
            error={receptionistConfigError}
            isAdmin={isReceptionistAdmin}
            isDiscarding={isDiscarding}
            isOpeningReview={isOpeningPublishReview}
            isPublishing={isPublishing}
            onCloseReview={() => setPublishReviewOpen(false)}
            onDiscard={discardDraft}
            onOpenReview={openPublishReview}
            onPublish={publishDraft}
            reviewOpen={publishReviewOpen}
            success={publishSuccess}
            voiceNames={voiceNames}
            calendarEventNames={calendarEventNames}
          />
        </aside>}
        <div className="receptionist-workspace-content">
      {setupGoal && receptionistConfigError ? <div className="workflow-alert" role="alert">{receptionistConfigError}<button type="button" onClick={refreshReceptionistConfiguration}>Retry saving settings</button></div> : null}
      {!setupGoal && <div className="receptionist-settings-tabs" role="tablist" aria-label="Receptionist settings">
        {[
          { id: 'voice', label: 'Voice & Greeting', pending: voiceChanges },
          { id: 'handling', label: 'Call Handling & Workflows', pending: handlingChanges },
        ].map(tab => <button key={tab.id} type="button" role="tab"
          id={`receptionist-tab-${tab.id}`} aria-controls={`receptionist-panel-${tab.id}`}
          aria-selected={settingsTab === tab.id} tabIndex={settingsTab === tab.id ? 0 : -1}
          ref={element => { settingsTabRefs.current[tab.id] = element; }}
          onClick={() => setSettingsTab(tab.id)} onKeyDown={event => handleSettingsTabKey(event, tab.id)}>
          <span>{tab.label}</span>
          {tab.pending > 0 && <span className="receptionist-tab-pending" aria-label={`${tab.pending} unpublished changes`}>{tab.pending}</span>}
        </button>)}
      </div>}
      {!setupGoal && <div id="receptionist-panel-voice" role="tabpanel" aria-labelledby="receptionist-tab-voice"
        tabIndex={0} hidden={settingsTab !== 'voice'} className="receptionist-settings-panel">
      <div className="receptionist-basics">
        <GreetingSection
          disabled={!isReceptionistAdmin || !receptionistConfiguration}
          draftSaveStatus={draftSaveStatus}
          greeting={receptionistConfiguration?.draft?.greeting ?? organization.greeting ?? ''}
          onDraftChange={changes => queueDraft(changes)}
        />
        <LanguageSection
          disabled={!isReceptionistAdmin || !receptionistConfiguration}
          settings={receptionistConfiguration?.draft?.languageSettings}
          options={receptionistConfiguration?.languageOptions || []}
          onChange={languageSettings => queueDraft({ languageSettings }, true)}
        />
        <SelectVoiceSection
          defaultLanguage={receptionistConfiguration?.draft?.languageSettings?.defaultLanguage || 'en'}
          disabled={!isReceptionistAdmin || !receptionistConfiguration}
          greeting={receptionistConfiguration?.draft?.greeting ?? organization.greeting ?? ''}
          onDraftChange={changes => queueDraft(changes, true)}
          onVoiceOptionsLoaded={handleVoiceOptionsLoaded}
          voiceId={receptionistConfiguration?.draft?.voiceId ?? organization.voice_id ?? ''}
        />
      </div>
      </div>}

      <div id={setupGoal ? undefined : 'receptionist-panel-handling'} role={setupGoal ? undefined : 'tabpanel'}
        aria-labelledby={setupGoal ? undefined : 'receptionist-tab-handling'} tabIndex={setupGoal ? undefined : 0}
        hidden={!setupGoal && settingsTab !== 'handling'} className="receptionist-settings-panel">
      <WorkflowManager
        setupGoal={setupGoal}
        onBusyChange={setWorkflowBusy}
        orgMember={orgMember}
        activeBuiltin={activeTab === 'schedule' ? 'appointment' : activeTab}
        onSelectBuiltin={selectBuiltInWorkflow}
        onCustomEditorChange={setIsCustomWorkflowOpen}
        onDraftStateChange={refreshReceptionistConfiguration}
        publishedVersion={receptionistConfiguration?.version || 0}
        refreshVersion={workflowRefreshVersion}
      />

      {/* Scheduling Tab */}
      <div id="workflow-built-in-schedule" className={`tab-content ${activeTab === 'schedule' && !isCustomWorkflowOpen ? 'visible-tab' : 'hidden-tab'}`}>
        <div className="workflow-outcome-settings">
          {calendarSettingsError && <div className="workflow-alert" role="alert">
            {calendarSettingsError}
            <button type="button" disabled={Boolean(calendarAction)} onClick={() => getCalendarSettings()}>Retry</button>
          </div>}
          <div className="workflow-outcome-heading"><span>Appointment destination</span><p>Choose how your connected calendar is used for this Receptionist workflow.</p></div>
          <div className={`appointment-calendar-destination ${isBothDisconnected ? 'is-empty' : ''}`}>
            <div className="appointment-calendar-provider">
              {isGoogleConnected || isCalendlyConnected ? (
                <>
                  <img src={isCalendlyConnected ? '/images/calendly_icon.png' : '/images/google_calendar_icon.png'} alt="" aria-hidden="true" />
                  <div><strong>{isCalendlyConnected ? 'Calendly' : 'Google Calendar'}</strong><span>{isCalendlyConnected ? 'Connected event type' : 'Primary calendar'}</span></div>
                  <span className="status-pill ok">Connected</span>
                </>
              ) : (
                <div className="appointment-calendar-empty"><strong>No calendar connected</strong><span>Connect Google Calendar or Calendly before enabling appointment booking.</span></div>
              )}
            </div>
            {isCalendlyConnected && calendarEventTypes.length > 0 ? (
              <label className="appointment-calendar-event-select" htmlFor="calendly-event-select">
                <span>Event type</span>
                <select id="calendly-event-select" value={selectedCalendarId} onChange={handleEventTypeChange} disabled={!isReceptionistAdmin || !receptionistConfiguration || Boolean(calendarAction) || isPublishing || isDiscarding || publishReviewOpen}>
                  {!selectedCalendarId && <option value="" disabled>Select an event type</option>}
                  {selectedCalendarId && !calendarEventTypes.some(event => event.uri === selectedCalendarId) && <option value={selectedCalendarId} disabled>Event type unavailable</option>}
                  {calendarEventTypes.map(event => <option key={event.uri} value={event.uri}>{calendarEventNames[event.uri]}</option>)}
                </select>
                {calendarAction === 'event-type' ? <small role="status"><LoadingIndicator label="Saving to draft…" /></small> : null}
              </label>
            ) : null}
            {calendarAction === 'calendar-settings' ? <span className="receptionist-operation-status" role="status"><LoadingIndicator label="Loading calendar options…" /></span> : null}
            {!setupGoal && <Link className="appointment-calendar-manage" to="/integrations">Manage integrations →</Link>}
          </div>

          {/* Everything below the calendar cards */}
          {isReceptionistAdmin && <MeetingLocationSettings
            organizationId={organization.id}
            capabilities={calendarSettings}
            value={appointmentDraft.meetingSettings}
            onChange={meetingSettings => queueDraft({ meetingSettings })}
            disabled={Boolean(calendarAction) || !receptionistConfiguration || isPublishing || isDiscarding}
          />}
          <div className="below-calendar-section-wrapper" style={{ position: 'relative' }}>

            <MeetingConfirmationSettings
              capabilities={calendarSettings}
              value={appointmentDraft.meetingSettings}
              onChange={meetingSettings => queueDraft({ meetingSettings })}
              disabled={!isReceptionistAdmin || Boolean(calendarAction) || !receptionistConfiguration || isPublishing || isDiscarding || publishReviewOpen}
            >
            <label
              className={`appointment-text-option ${isScheduleTextConfirmEnabled ? 'is-enabled' : ''} ${isBothDisconnected ? 'is-disabled' : ''}`}
            >
              <span className="appointment-text-option-icon" aria-hidden="true">SMS</span>
              <span className="appointment-text-option-copy">
                <span className="appointment-text-option-title">
                  <strong>Offer confirmation texts</strong>
                  <small>{isScheduleTextConfirmEnabled ? 'On' : 'Off'}</small>
                </span>
                <span>Offer appointment details by text after booking. Sent only if the caller agrees.</span>
                {calendarAction === 'scheduleTextConfirm' ? <span className="receptionist-operation-status" role="status"><LoadingIndicator label="Saving to draft…" /></span> : null}
              </span>
              <span className="appointment-text-option-switch">
                <input
                  data-say-id="schedule-text-confirm-toggle"
                  type="checkbox"
                  role="switch"
                  aria-label="Offer appointment confirmation texts"
                  checked={isScheduleTextConfirmEnabled}
                  onChange={() => handleToggleFeature('scheduleTextConfirm')}
                  disabled={isBothDisconnected || Boolean(calendarAction)}
                />
                <span aria-hidden="true" />
              </span>
            </label>
            </MeetingConfirmationSettings>

            <div className='y-space-40' />
            <section className={`appointment-availability-card ${isBothDisconnected ? 'is-disabled' : ''}`}>
              <div className="appointment-availability-heading">
                <div className="appointment-availability-icon" aria-hidden="true"><span /><span /></div>
                <div>
                  <h3>Appointment hours</h3>
                  <p>Tell Sayable when customers can book. Describe it naturally, then review the exact weekly schedule.</p>
                </div>
                <button data-say-id="availability-modal-open-btn" className="appointment-availability-button" onClick={openAvailabilityModal} disabled={isBothDisconnected || Boolean(calendarAction)}>
                  <BusyContent busy={calendarAction === 'availability'} label="Opening schedule…">Set appointment hours</BusyContent>
                </button>
              </div>
              <div className="appointment-availability-meta">
                <span><small>Timezone</small><strong>{appointmentDraft.timezone || latestOrganization.timezone || 'Not set'}</strong></span>
                <span><small>Advance notice</small><strong>{appointmentDraft.leadTime ?? latestOrganization.lead_time ?? '—'}{(appointmentDraft.leadTime ?? latestOrganization.lead_time) !== undefined && (appointmentDraft.leadTime ?? latestOrganization.lead_time) !== null ? ' days' : ''}</strong></span>
                <span><small>Meeting length</small><strong>{appointmentDraft.duration || latestOrganization.duration || '—'}{appointmentDraft.duration || latestOrganization.duration ? ' min' : ''}</strong></span>
              </div>
              <div className="appointment-availability-week">
                <AvailabilityTable availability={appointmentDraft.availability ?? latestOrganization.availability} />
              </div>
              {isBothDisconnected ? <p className="appointment-availability-disabled-note">Connect a calendar in Integrations before setting appointment hours.</p> : null}
            </section>

            {showAvailabilityModal && (
              <div className="availability-modal-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setShowAvailabilityModal(false); }}>
                <div className="availability-modal" role="dialog" aria-modal="true" aria-labelledby="availability-modal-title">
                  <header className="availability-modal-header">
                    <div><h2 id="availability-modal-title">Set appointment hours</h2><p>Say it naturally. You’ll see and approve the exact schedule before anything is saved.</p></div>
                    <button type="button" data-say-id="availability-modal-close-btn" className="availability-modal-close" onClick={() => setShowAvailabilityModal(false)} aria-label="Close appointment hours">×</button>
                  </header>
                  <SchedulePicker
                    organization={appointmentDraftOrganization}
                    onClose={() => setShowAvailabilityModal(false)}
                    onSave={changes => queueDraft(changes, true)}
                  />
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Transfer Calls Tab */}
      <div id="workflow-built-in-transfer" className={`tab-content ${activeTab === 'transfer' && !isCustomWorkflowOpen ? 'visible-tab' : 'hidden-tab'}`}>
        <div className="workflow-outcome-settings workflow-routing-settings">
          <div className="workflow-outcome-heading"><span>Routing destination</span><p>Calls route to the people and phone numbers configured for your organization.</p></div>
          {organization.subscription_type !== 'solo' ? (
            setupGoal ? <p>Set destinations and availability in the team directory below. Unanswered calls fall back to message taking.</p> : <a className="workflow-outcome-link" href="/organization">Manage organization routing →</a>
          ) : (
            <span className="workflow-outcome-note">Upgrade your plan to resume call routing.</span>
          )}
        </div>
      </div>

      </div>
        </div>
      </div>
    </div>
  );
}

export default FeatureSection;
